import type { FundModel } from '../../../../types';
import clsx from 'clsx';
import { formatDate } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router';
import { toAbsoluteStatic } from '../../../../_mesomb/helpers';
import Stat from '../../../../_mesomb/partials/components/Stat.tsx';
import FundTarget from '../../fund/components/FundTarget.tsx';

function FundOutlet({ fund }: { fund: FundModel }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  if (pathname.endsWith('contribute')) {
    return (
      <div className='d-flex flex-column flex-root'>
        <div className='d-flex flex-column flex-center flex-column-fluid'>
          <div className='d-flex flex-column p-10'>
            <Outlet />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className='d-flex flex-column flex-root'>
      <div className='d-flex flex-column flex-center flex-column-fluid'>
        <div className='d-flex flex-column p-10'>
          <div className='card w-lg-1000px'>
            <div className='card-body pt-9 pb-0'>
              <div className='position-relative mb-17'>
                <div className='overlay overlay-show'>
                  <div
                    className='bgi-no-repeat bgi-position-center bgi-size-cover card-rounded min-h-250px'
                    style={{
                      backgroundImage: `url('${toAbsoluteStatic((typeof fund.profile === 'string' ? fund.profile : fund.profile?.path) ?? '')}')`,
                    }}
                  ></div>
                  <div
                    className='overlay-layer rounded bg-black'
                    style={{ opacity: 0.4 }}
                  ></div>
                </div>
                <div className='position-absolute text-white mb-8 ms-10 bottom-0'>
                  <h3 className='text-white fs-2qx fw-bold mb-3 m'>
                    {fund.title}
                  </h3>
                  <div className='fs-5 fw-semibold'>
                    {fund.category.title} {t('for')} {fund.beneficiaries}
                  </div>
                </div>
                {fund.is_open && (
                  <div
                    className='position-absolute text-white mb-8 me-10 bottom-0'
                    style={{ right: 0 }}
                  >
                    <a
                      href={`/f/${fund.slug}/donate`}
                      className='btn btn-primary'
                      id='kt_careers_submit_button'
                    >
                      {t('I Participate')}
                    </a>
                  </div>
                )}
              </div>
              <div className='d-flex flex-wrap flex-stack'>
                <div className='d-flex flex-grow-1 pe-8'>
                  {!fund.hide_collected && (
                    <Stat
                      label={t('Collected')}
                      value={fund.collected_amount}
                      prefix={fund.currency}
                      type={'counter'}
                    />
                  )}
                  {fund.is_open && (
                    <>
                      <Stat
                        label={t('Start Date')}
                        value={formatDate(fund.created_at, 'PP')}
                      />
                      {fund.expiry_date && (
                        <Stat label={t('Close')} value={fund.time_left ?? "—"} />
                      )}
                    </>
                  )}
                </div>
                {!fund.hide_target && (fund.target ?? 0) > 0 && (
                  <FundTarget fund={fund} className={'w-200px w-sm-300px'} />
                )}
              </div>

              <div className='separator'></div>
              <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold mb-5'>
                <li className='nav-item'>
                  <Link
                    to={`/contribute/${fund.slug}`}
                    className={clsx('nav-link text-active-primary py-5 me-6', {
                      active: pathname.endsWith(fund.slug),
                    })}
                  >
                    {t('Overview')}
                  </Link>
                </li>
                <li className='nav-item'>
                  <Link
                    to={`/contribute/${fund.slug}/contributions`}
                    className={clsx('nav-link text-active-primary py-5 me-6', {
                      active: pathname.endsWith('contributions'),
                    })}
                  >
                    {t('Contributions')}
                  </Link>
                </li>
                <li className='nav-item'>
                  <Link
                    to={`/contribute/${fund.slug}/messages`}
                    className={clsx('nav-link text-active-primary py-5 me-6', {
                      active: pathname.endsWith('messages'),
                    })}
                  >
                    {t('Messages')}
                  </Link>
                </li>
              </ul>

              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FundOutlet;
